import React, { CSSProperties, Fragment, FunctionComponent, useLayoutEffect, useRef, useState } from 'react';
import { Button, Card, CustomScrollbar, CustomScrollbarRef, Icon, Responsive } from 'rs-emd-ui-atoms';
import { getLabel } from '../../..';
import { includeVat } from '../../../helpers/user-settings.utils';
import { ILabel } from '../../../models/common/label.model';
import { Locale } from '../../../models/master/context-info.model';
import { ISearchSort } from '../../../models/plp/input/search-selector.model';
import { IPLPCategoryItem } from '../../../models/plp/plp-category-item.model';
import { IPLPProducts } from '../../../models/plp/plp-products.model';
import { IPLPConfig } from '../../../models/plp/product-list-page.model';
import { IPLPService } from '../../../services/plp/plp-service';
import { IPricingService } from '../../../services/pricing/pricing-service';
import { IProductService } from '../../../services/product/product-service';
import { ListProductCard } from './../';
import styles from './list-view-component.module.scss';

type ListViewProps = {
  labels?: ILabel[] | null;
  productsArea?: IPLPProducts | null;
  stockService: IProductService;
  plpService: IPLPService;
  pricingService: IPricingService;
  sort?: ISearchSort;
  config: IPLPConfig;
  categoriesArea: IPLPCategoryItem[];
  scrollPosition: number;
  locale: Locale;
  productService: IProductService;
  addToBasket: (stockCode: string, qty: number) => Promise<void>;
  clearFilters: () => void;
  updateProducts: (sort: ISearchSort) => void;
  onAddToPartsList: (stockCode: string, qty: number) => void;
  setScrollPosition: (x: number) => void;
};

export const ListView: FunctionComponent<ListViewProps> = (props: ListViewProps) => {
  const scrollRef = useRef<CustomScrollbarRef>(null);
  const attributesHeaderRef = useRef<HTMLDivElement>(null);
  const attributesProductRef = useRef<Array<HTMLDivElement | null>>([]);
  const [incVat, setIncVat] = useState<boolean>(false);

  const attributesLength = props.productsArea?.categoryAttributes?.attributes.length ?? 0;
  const isSRP = props.categoriesArea.length > 0;

  useLayoutEffect(() => {
    setIncVat(includeVat());
  }, []);

  useLayoutEffect(() => {
    scrollRef.current?.updateScroll();
  }, [props.productsArea?.categoryAttributes?.attributes]);

  useLayoutEffect(() => {
    scrollRef.current?.setScrollLeft(props.scrollPosition);
  }, [props.scrollPosition]);

  function onScoll() {
    const scrollLeft = scrollRef.current?.getScrollLeft() ?? 0;
    if (attributesHeaderRef.current && attributesProductRef.current) {
      attributesHeaderRef.current.scrollLeft = scrollLeft;
      attributesProductRef.current.forEach((e) => {
        if (e) e.scrollLeft = scrollLeft;
      });
    }
    props.setScrollPosition(scrollLeft);
  }

  return (
    <div className={styles['lview-container']} style={{ '--number-of-attributes': attributesLength } as CSSProperties}>
      {(props.productsArea?.products?.length ?? 0) > 0 && (
        <>
          {/* Header shown only for PLP */}
          {!isSRP && (
            <div className={styles['header-wrapper']}>
              <Responsive breakpoints={['xl-desktop']}>
                <div className={styles.header}>
                  <div className={styles.headings}>
                    {attributesLength > 3 && (
                      <div className={styles.scrollbar}>
                        <CustomScrollbar
                          ref={scrollRef}
                          color='light-grey'
                          showOnLoad={true}
                          width='thick'
                          showXButtons={true}
                          disableHoverAnimation={true}
                          onScroll={(e: any) => onScoll()}>
                          <div
                            className={styles['scroll-inner']}
                            style={{
                              width: `${attributesLength * 178}px`, //cannot use css vars due to IE11 :(
                              height: 1,
                            }}></div>
                        </CustomScrollbar>
                      </div>
                    )}
                    <p className={`${styles.details} ${styles.heading}`}>{getLabel(props.labels, 'header_product_details')}</p>
                    <p className={`${styles.price} ${styles.heading}`}>
                      {getLabel(props.labels, 'price')}
                      {getSortIcon('price')}
                    </p>
                    <div className={styles.attributes} ref={attributesHeaderRef}>
                      {props.productsArea?.categoryAttributes?.attributes.map((a) => {
                        return (
                          <p className={styles.heading} key={a.id}>
                            {a.name} {getSortIcon(a.id)}
                          </p>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </Responsive>
            </div>
          )}
          <div className={`${styles.products} ${!isSRP ? styles.grid : ''}`}>
            {props.productsArea?.products.map((product, index) => {
              return (
                <Fragment key={product.title.stockCode}>
                  <ListProductCard
                    style={{ msGridColumn: 1, msGridRow: index + 1 } as CSSProperties}
                    productService={props.stockService}
                    plpService={props.plpService}
                    pricingService={props.pricingService}
                    labels={props.labels}
                    product={product}
                    addToBasket={props.addToBasket}
                    config={props.config}
                    incVat={incVat}
                    isLast={index === (props.productsArea?.products.length ?? 0) - 1}
                    isSRP={isSRP}
                    onAddToPartsList={props.onAddToPartsList}
                    locale={props.locale}
                  />

                  {!isSRP && (
                    <div
                      className={styles.attributes}
                      ref={(el) => (attributesProductRef.current[index] = el)}
                      style={{ msGridColumn: 2, msGridRow: index + 1 } as CSSProperties}>
                      {product.categoryAttributes?.map((a) => {
                        //brand
                        if (a.attributeNameId === '4390') {
                          return (
                            <a
                              className={styles.attribute}
                              key={a.attributeNameId}
                              href={`/productlist/search?query=${encodeURIComponent(a.value.toLowerCase())}`}
                              onClick={() => props.productService.brandTrackerClickEvent('BrandSearchPLP', a.value)}>
                              {a.value}
                            </a>
                          );
                        } else {
                          return (
                            <div className={styles.attribute} key={a.attributeNameId}>
                              {a.value}
                            </div>
                          );
                        }
                      })}
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        </>
      )}
      {!props.productsArea?.products?.length && (
        <Card className={styles['no-products']}>
          <p>{getLabel(props.labels, 'filters_no_results_message')}</p>{' '}
          <Button text={getLabel(props.labels, 'filters_clear_all')} onClick={props.clearFilters}></Button>
        </Card>
      )}
    </div>
  );

  function getSortIcon(sortField: string) {
    let iconName: 'chev-sort' | 'chev-up' | 'chev-down';
    let iconSize = 24;
    if (props.sort?.sortField !== sortField) {
      iconName = 'chev-sort';
    } else {
      iconName = props.sort?.direction === 0 ? 'chev-up' : 'chev-down';
      iconSize = 20;
    }

    return <Icon name={iconName} width={iconSize} height={24} onClick={() => sortProducts(sortField)}></Icon>;
  }

  function sortProducts(sortField: string) {
    let direction: 0 | 1 = 0;
    if (props.sort) direction = props.sort.sortField === sortField ? (props.sort.direction ? 0 : 1) : 0;

    let sort: ISearchSort = {
      sortField: sortField,
      direction: direction,
    };

    props.updateProducts(sort);
  }
};
